import { router } from '@inertiajs/react';
import { useState, useEffect, useCallback } from 'react';
import { Editor } from '@/components/Editor';
import { MediaUpload, MediaLibrary, ImageSizeSelector, type UploadedMedia, type ImageSize } from '@/components/Media';
import type { HexData } from '@/components/HexMap';
import { TileSelectionModal } from '@/components/TileSelectionModal';
import { getTileUrl, type Domain } from '@/components/HexMap/tiles';
import HexLayout, { type SidebarContext } from '@/layouts/HexLayout';
import { HexQuickLinks } from '@/components/HexQuickLinks/HexQuickLinks';
import { HexLibrarySidebar } from '@/components/HexLibrary/HexLibrarySidebar';
import type { HexLibrary, HexLibraryBook, HexLibraryChapter } from '@/components/HexLibrary/types';
import { BookModal } from '@/components/Hex/Modals/BookModal';
import { ChapterModal } from '@/components/Hex/Modals/ChapterModal';
import { NewPageModal } from '@/components/Hex/Modals/NewPageModal';

// Terrain editing removed from Hex Settings

/** Represents a page within a hex */
interface HexPage {
    id: number;
    name: string;
    content?: string | null;
    order: number;
}

interface Props {
    hex: HexData & {
        /** Legacy markdown content (deprecated) */
        content: string | null;
        collaborators: Array<{ id: number; username: string }>;
        isOpenHex?: boolean;
        isEnclave?: boolean;
    };
    /** All pages for sidebar navigation */
    pages: HexPage[];
    /** Currently selected page for editing */
    currentPage: HexPage | null;
    /** Hierarchical library structure (books -> chapters -> pages) */
    library: HexLibrary;
    canEdit: boolean;
    isOwner: boolean;
    isGm: boolean;
}

/**
 * Hex edit page - allows owner/collaborators to edit hex settings and page content.
 * Supports multi-page editing via the page query parameter.
 */
export default function HexEdit({ hex, pages, currentPage, library, canEdit, isOwner, isGm }: Props) {
    const [newCollaborator, setNewCollaborator] = useState('');
    const [collaboratorError, setCollaboratorError] = useState('');
    const [showNewPageModal, setShowNewPageModal] = useState(false);
    const [newPageName, setNewPageName] = useState('');
    const [isCreatingPage, setIsCreatingPage] = useState(false);
    const [newPageChapterId, setNewPageChapterId] = useState<number | null>(null);
    const [showTileModal, setShowTileModal] = useState(false);

    // State for library modals
    const [showBookModal, setShowBookModal] = useState(false);
    const [editingBook, setEditingBook] = useState<HexLibraryBook | null>(null);
    const [bookTitle, setBookTitle] = useState('');
    const [bookDescription, setBookDescription] = useState('');
    const [bookHue, setBookHue] = useState(260);
    const [isSavingBook, setIsSavingBook] = useState(false);

    const [showChapterModal, setShowChapterModal] = useState(false);
    const [editingChapter, setEditingChapter] = useState<HexLibraryChapter | null>(null);
    const [editingChapterBookId, setEditingChapterBookId] = useState<number | null>(null);
    const [chapterTitle, setChapterTitle] = useState('');
    const [isSavingChapter, setIsSavingChapter] = useState(false);

    // State for hex settings (name)
    const [hexData, setHexData] = useState({
        name: hex.name,
        content: hex.content || '', // Legacy field, kept for backwards compatibility
    });
    const [hexProcessing, setHexProcessing] = useState(false);
    const [hexErrors, setHexErrors] = useState<{ name?: string }>({});

    // State for page content editing
    const [pageData, setPageData] = useState({
        name: currentPage?.name || '',
        content: currentPage?.content || '',
    });
    const [pageProcessing, setPageProcessing] = useState(false);
    const [pageErrors, setPageErrors] = useState<{ name?: string; content?: string }>({});

    // State for media library
    const [mediaList, setMediaList] = useState<UploadedMedia[]>([]);
    const [mediaLoading, setMediaLoading] = useState(false);

    // State for image drop size selector
    const [pendingImageDrop, setPendingImageDrop] = useState<{
        media: UploadedMedia;
        position: { x: number; y: number };
    } | null>(null);

    // Fetch media when hex or page changes
    useEffect(() => {
        const fetchMedia = async () => {
            setMediaLoading(true);
            try {
                const url = currentPage
                    ? `/hex-media/${hex.id}?page_id=${currentPage.id}`
                    : `/hex-media/${hex.id}`;
                const response = await fetch(url, {
                    headers: { 'Accept': 'application/json' },
                });
                if (response.ok) {
                    const data = await response.json();
                    setMediaList(data.media || []);
                }
            } catch (err) {
                console.error('Failed to fetch media:', err);
            } finally {
                setMediaLoading(false);
            }
        };
        fetchMedia();
    }, [hex.id, currentPage?.id]);

    // Keep form state in sync when navigating between pages.
    useEffect(() => {
        setPageData({
            name: currentPage?.name || '',
            content: currentPage?.content || '',
        });
    }, [currentPage?.id, currentPage?.name, currentPage?.content]);

    // Handle new media upload
    const handleMediaUpload = (media: UploadedMedia) => {
        setMediaList(prev => [media, ...prev]);
    };

    // Handle media deletion
    const handleMediaDelete = (mediaId: number) => {
        setMediaList(prev => prev.filter(m => m.id !== mediaId));
    };

    /**
     * Handle image drop from MediaLibrary onto Editor.
     * Shows the size selector at the drop position.
     */
    const handleImageDrop = useCallback((media: UploadedMedia, position: { x: number; y: number }) => {
        setPendingImageDrop({ media, position });
    }, []);

    /**
     * Handle size selection from ImageSizeSelector.
     * Inserts the markdown image at the cursor position.
     */
    const handleImageSizeSelect = useCallback((size: ImageSize, media: UploadedMedia) => {
        // Get the appropriate URL based on selected size
        const url = media.urls[size];
        const altText = media.alt_text || media.original_filename;
        const markdown = `![${altText}](${url})`;

        // Insert at cursor position (append to content if no cursor)
        setPageData(prev => ({
            ...prev,
            content: prev.content ? `${prev.content}\n\n${markdown}` : markdown,
        }));

        // Clear the pending drop
        setPendingImageDrop(null);
    }, []);

    /**
     * Cancel the image size selection.
     */
    const handleImageDropCancel = useCallback(() => {
        setPendingImageDrop(null);
    }, []);

    // Handle hex settings form submission using router.put directly
    const handleHexSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setHexProcessing(true);
        setHexErrors({});
        router.put(`/hex/${hex.id}`, hexData, {
            onError: (errors) => setHexErrors(errors as { name?: string }),
            onFinish: () => setHexProcessing(false),
        });
    };

    // Handle page content save using fetch directly
    const handlePageSubmit = async () => {
        console.log('handlePageSubmit called');
        console.log('currentPage:', currentPage);
        console.log('hex.id:', hex.id);
        if (!currentPage) {
            console.log('No currentPage, returning early');
            return;
        }
        const url = `/hex-pages/${hex.id}/${currentPage.id}`;
        console.log('PUT URL:', url);
        console.log('pageData:', pageData);
        setPageProcessing(true);
        setPageErrors({});

        try {
            // Get CSRF token from cookie
            const csrfToken = document.cookie
                .split('; ')
                .find(row => row.startsWith('XSRF-TOKEN='))
                ?.split('=')[1];

            const absoluteUrl = `${window.location.origin}${url}`;
            console.log('Making fetch request to:', absoluteUrl);
            const response = await fetch(absoluteUrl, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'X-XSRF-TOKEN': csrfToken ? decodeURIComponent(csrfToken) : '',
                },
                body: JSON.stringify(pageData),
                credentials: 'same-origin',
            });

            console.log('Response status:', response.status);

            if (response.ok) {
                console.log('Success');
                // Navigate to the view page after saving
                router.visit(`/hex/${hex.id}?page=${currentPage.id}&tab=library`);
            } else if (response.status === 422) {
                const data = await response.json();
                console.log('Validation errors:', data);
                if (data.errors) {
                    setPageErrors(data.errors);
                }
            } else {
                console.log('Error response:', await response.text());
            }
        } catch (error: any) {
            console.log('Fetch error:', error);
        } finally {
            setPageProcessing(false);
        }
    };

    // Add collaborator
    const handleAddCollaborator = () => {
        if (!newCollaborator.trim()) return;
        setCollaboratorError('');
        router.post(`/hex/${hex.id}/collaborators`, { username: newCollaborator }, {
            onSuccess: () => setNewCollaborator(''),
            onError: (errors) => setCollaboratorError(errors.username || 'Failed to add collaborator'),
        });
    };

    // Remove collaborator
    const handleRemoveCollaborator = (userId: number) => {
        router.delete(`/hex/${hex.id}/collaborators/${userId}`);
    };

    // Handle creating a new page
    const handleCreatePage = (e: React.FormEvent) => {
        e.preventDefault();
        if (!newPageName.trim() || isCreatingPage || !newPageChapterId) return;

        setIsCreatingPage(true);
        router.post(`/hex-pages/${hex.id}`, {
            name: newPageName.trim(),
            content: '',
            chapter_id: newPageChapterId,
        }, {
            onSuccess: () => {
                setShowNewPageModal(false);
                setNewPageName('');
                setNewPageChapterId(null);
            },
            onFinish: () => {
                setIsCreatingPage(false);
            },
        });
    };

    // Handle deleting a page
    const handleDeletePage = (pageId: number) => {
        if (!confirm('Are you sure you want to delete this page? This cannot be undone.')) {
            return;
        }
        router.delete(`/hex-pages/${hex.id}/${pageId}`);
    };

    const handleShowNewBookModal = useCallback(() => {
        setEditingBook(null);
        setBookTitle('');
        setBookDescription('');
        setBookHue(260);
        setShowBookModal(true);
    }, []);

    const handleShowEditBookModal = useCallback((book: HexLibraryBook) => {
        setEditingBook(book);
        setBookTitle(book.title);
        setBookDescription(book.description || '');
        setBookHue(book.hue);
        setShowBookModal(true);
    }, []);

    const handleSaveBook = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!bookTitle.trim() || isSavingBook) return;

        setIsSavingBook(true);

        if (editingBook) {
            router.patch(`/hex/${hex.id}/books/${editingBook.id}`, {
                title: bookTitle.trim(),
                description: bookDescription.trim() || null,
                hue: bookHue,
            }, {
                onSuccess: () => {
                    setShowBookModal(false);
                    setEditingBook(null);
                },
                onFinish: () => setIsSavingBook(false),
            });
        } else {
            router.post(`/hex/${hex.id}/books`, {
                title: bookTitle.trim(),
                description: bookDescription.trim() || null,
                hue: bookHue,
            }, {
                onSuccess: () => {
                    setShowBookModal(false);
                },
                onFinish: () => setIsSavingBook(false),
            });
        }
    }, [hex.id, bookTitle, bookDescription, bookHue, editingBook, isSavingBook]);

    const handleShowNewChapterModal = useCallback((bookId: number) => {
        setEditingChapter(null);
        setEditingChapterBookId(bookId);
        setChapterTitle('');
        setShowChapterModal(true);
    }, []);

    const handleShowEditChapterModal = useCallback((chapter: HexLibraryChapter, bookId: number) => {
        setEditingChapter(chapter);
        setEditingChapterBookId(bookId);
        setChapterTitle(chapter.title);
        setShowChapterModal(true);
    }, []);

    const handleSaveChapter = useCallback((e: React.FormEvent) => {
        e.preventDefault();
        if (!chapterTitle.trim() || isSavingChapter || !editingChapterBookId) return;

        setIsSavingChapter(true);

        if (editingChapter) {
            router.patch(`/hex/${hex.id}/books/${editingChapterBookId}/chapters/${editingChapter.id}`, {
                title: chapterTitle.trim(),
            }, {
                onSuccess: () => {
                    setShowChapterModal(false);
                    setEditingChapter(null);
                    setEditingChapterBookId(null);
                },
                onFinish: () => setIsSavingChapter(false),
            });
        } else {
            router.post(`/hex/${hex.id}/books/${editingChapterBookId}/chapters`, {
                title: chapterTitle.trim(),
            }, {
                onSuccess: () => {
                    setShowChapterModal(false);
                    setEditingChapterBookId(null);
                },
                onFinish: () => setIsSavingChapter(false),
            });
        }
    }, [hex.id, chapterTitle, editingChapter, editingChapterBookId, isSavingChapter]);

    const handleShowNewPageModal = useCallback((chapterId: number) => {
        setNewPageChapterId(chapterId);
        setNewPageName('');
        setShowNewPageModal(true);
    }, []);

    const handleShowCreateFirstPageModal = useCallback(() => {
        const firstChapterId = library.find(book => book.chapters.length > 0)?.chapters[0]?.id ?? null;

        if (!firstChapterId) {
            handleShowNewBookModal();
            return;
        }

        handleShowNewPageModal(firstChapterId);
    }, [library, handleShowNewBookModal, handleShowNewPageModal]);

    return (
        <HexLayout
            title={`Edit ${hex.name}`}
            hex={hex}
            isOwner={isOwner}
            backLink={{
                href: `/hex/${hex.id}${currentPage ? `?page=${currentPage.id}&tab=library` : ''}`,
                label: 'Back to View',
            }}
            sidebar={({ closeSidebar, isMobile }: SidebarContext) => (
                <>
                    {/* Quick Navigation Links - same as View page */}
                    <HexQuickLinks
                        hexId={hex.id}
                        isOwner={isOwner}
                        isOpenHex={hex.isOpenHex}
                        isEnclave={hex.isEnclave}
                        activeItem="library"
                        collapsible
                        closeSidebar={closeSidebar}
                        isMobile={isMobile}
                    />

                    {/* Library sidebar - hierarchical book/chapter/page structure */}
                    <HexLibrarySidebar
                        library={library}
                        currentPageId={currentPage?.id ?? null}
                        hexId={hex.id}
                        canEdit={canEdit}
                        isMobile={isMobile}
                        closeSidebar={closeSidebar}
                        onShowNewBookModal={handleShowNewBookModal}
                        onShowNewChapterModal={handleShowNewChapterModal}
                        onShowNewPageModal={handleShowNewPageModal}
                        onShowEditBookModal={handleShowEditBookModal}
                        onShowEditChapterModal={handleShowEditChapterModal}
                        pageHrefBuilder={(hexId, pageId) => `/hex/${hexId}/edit?page=${pageId}`}
                    />

                    {/* Hex Settings section */}
                    <div className="p-4" style={{ borderTop: '1px solid #5c4a3d' }}>
                        <h2
                            className="text-xs font-bold uppercase tracking-wider mb-3"
                            style={{ color: '#D4A574' }}
                        >
                            Hex Settings
                        </h2>
                        <form onSubmit={handleHexSubmit} className="space-y-4">
                            {/* Name input */}
                            <div>
                                <label htmlFor="hex-name" className="block text-xs font-medium mb-1" style={{ color: '#A67C52' }}>
                                    Name
                                </label>
                                <input
                                    type="text"
                                    id="hex-name"
                                    value={hexData.name}
                                    onChange={(e) => setHexData(prev => ({ ...prev, name: e.target.value }))}
                                    className="w-full px-3 py-1.5 text-sm rounded focus:outline-none focus:ring-2 focus:ring-amber-500 caret-amber-100"
                                    style={{
                                        backgroundColor: '#2f231a',
                                        border: '1px solid #5c4a3d',
                                        color: '#F5DEB3',
                                    }}
                                    required
                                />
                                {hexErrors.name && (
                                    <p className="mt-1 text-xs text-red-400">{hexErrors.name}</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={hexProcessing}
                                className="w-full px-3 py-1.5 text-sm rounded transition-colors cursor-pointer disabled:cursor-not-allowed"
                                style={{
                                    backgroundColor: hexProcessing ? '#3d2e22' : '#5D4E37',
                                    border: '1px solid #6B5B47',
                                    color: '#F5DEB3',
                                }}
                            >
                                {hexProcessing ? 'Saving...' : 'Save Settings'}
                            </button>
                        </form>

                        {/* Tile Image Section */}
                        <div className="mt-4 pt-4" style={{ borderTop: '1px solid #5c4a3d' }}>
                            <label className="block text-xs font-medium mb-2" style={{ color: '#A67C52' }}>
                                Map Tile
                            </label>
                            {hex.domain && hex.tileImage ? (
                                <div className="flex items-center gap-3 mb-2">
                                    {(() => {
                                        const url = getTileUrl(hex.domain as Domain, hex.tileImage);
                                        return url ? (
                                            <img
                                                src={url}
                                                alt="Current tile"
                                                className="w-12 h-12 rounded object-cover"
                                                style={{ border: '1px solid #5c4a3d' }}
                                            />
                                        ) : (
                                            <div
                                                className="w-12 h-12 rounded flex items-center justify-center"
                                                style={{ border: '1px solid #5c4a3d', backgroundColor: '#2f231a' }}
                                            >
                                                <span style={{ color: '#6B5B47' }} className="text-xs">?</span>
                                            </div>
                                        );
                                    })()}
                                    <div className="text-xs capitalize" style={{ color: '#A67C52' }}>
                                        {hex.domain}
                                    </div>
                                </div>
                            ) : (
                                <p className="text-xs mb-2" style={{ color: '#6B5B47' }}>No tile set</p>
                            )}
                            <button
                                type="button"
                                onClick={() => setShowTileModal(true)}
                                className="w-full px-3 py-1.5 text-sm rounded transition-colors cursor-pointer"
                                style={{
                                    background: 'linear-gradient(180deg, #4D7A4D 0%, #3D6A3D 50%, #2D5A2D 100%)',
                                    border: '2px solid #5D8A5D',
                                    color: '#E8F5E8',
                                }}
                            >
                                {hex.domain && hex.tileImage ? 'Change Tile' : 'Set Tile Image'}
                            </button>
                        </div>
                    </div>

                    {/* Collaborator management (owner only) */}
                    {isOwner && (
                        <div className="p-4" style={{ borderTop: '1px solid #5c4a3d' }}>
                            <h2
                                className="text-xs font-bold uppercase tracking-wider mb-3"
                                style={{ color: '#D4A574' }}
                            >
                                Collaborators
                            </h2>

                            {/* Current collaborators */}
                            {hex.collaborators.length > 0 ? (
                                <ul className="space-y-1 mb-3">
                                    {hex.collaborators.map((collab) => (
                                        <li
                                            key={collab.id}
                                            className="flex items-center justify-between px-2 py-1 rounded text-sm"
                                            style={{ backgroundColor: '#2f231a', border: '1px solid #5c4a3d' }}
                                        >
                                            <span style={{ color: '#F5DEB3' }}>{collab.username}</span>
                                            <button
                                                onClick={() => handleRemoveCollaborator(collab.id)}
                                                className="text-red-400 hover:text-red-300 text-xs cursor-pointer"
                                            >
                                                ×
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-xs mb-3" style={{ color: '#6B5B47' }}>No collaborators yet.</p>
                            )}

                            {/* Add collaborator form */}
                            <div className="flex gap-1">
                                <input
                                    type="text"
                                    value={newCollaborator}
                                    onChange={(e) => setNewCollaborator(e.target.value)}
                                    placeholder="Username"
                                    className="flex-1 px-2 py-1 text-sm rounded focus:outline-none focus:ring-2 focus:ring-amber-500 caret-amber-100"
                                    style={{
                                        backgroundColor: '#2f231a',
                                        border: '1px solid #5c4a3d',
                                        color: '#F5DEB3',
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={handleAddCollaborator}
                                    className="px-2 py-1 text-sm rounded transition-colors cursor-pointer"
                                    style={{
                                        background: 'linear-gradient(180deg, #4D7A4D 0%, #3D6A3D 50%, #2D5A2D 100%)',
                                        border: '2px solid #5D8A5D',
                                        color: '#E8F5E8',
                                    }}
                                >
                                    Add
                                </button>
                            </div>
                            {collaboratorError && (
                                <p className="mt-1 text-xs text-red-400">{collaboratorError}</p>
                            )}
                        </div>
                    )}
                </>
            )}
        >
            {/* Main content area - Page editor */}
            <div className="p-8">
                {currentPage ? (
                            <div className="flex gap-6">
                                {/* Left side - Editor */}
                                <div className="flex-1 min-w-0">
                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            handlePageSubmit();
                                        }}
                                    >
                                        {/* Page header with save button */}
                                        <div className="flex items-center justify-between mb-6">
                                            <div className="flex-1">
                                                <label htmlFor="page-name" className="block text-sm font-medium text-gray-400 mb-1">
                                                    Page Name
                                                </label>
                                                <input
                                                    type="text"
                                                    id="page-name"
                                                    value={pageData.name}
                                                    onChange={(e) => setPageData(prev => ({ ...prev, name: e.target.value }))}
                                                    className="w-full max-w-md px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                                                />
                                                {pageErrors.name && (
                                                    <p className="mt-1 text-sm text-red-400">{pageErrors.name}</p>
                                                )}
                                            </div>
                                            <button
                                                type="submit"
                                                disabled={pageProcessing}
                                                className="ml-4 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-600/50 text-white rounded-lg transition-colors"
                                            >
                                                {pageProcessing ? 'Saving...' : 'Save Page'}
                                            </button>
                                        </div>

                                        {/* Page content editor */}
                                        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
                                            <label className="block text-sm font-medium text-gray-300 mb-3">
                                                Content
                                            </label>
                                            <Editor
                                                value={pageData.content}
                                                onChange={(value) => setPageData(prev => ({ ...prev, content: value }))}
                                                placeholder="Write your page content..."
                                                height={500}
                                                onImageDrop={handleImageDrop}
                                            />
                                            {pageErrors.content && (
                                                <p className="mt-2 text-sm text-red-400">{pageErrors.content}</p>
                                            )}
                                        </div>
                                    </form>
                                </div>

                                {/* Right side - Media upload and library */}
                                <aside className="w-72 flex-shrink-0">
                                    <div className="bg-slate-800 rounded-lg border border-slate-700 p-4 sticky top-0">
                                        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
                                            Page Images
                                        </h3>

                                        {/* Upload area */}
                                        <MediaUpload
                                            uploadUrl={`/hex-media/${hex.id}`}
                                            pageId={currentPage.id}
                                            onUploadComplete={handleMediaUpload}
                                            onUploadError={(err) => console.error('Upload error:', err)}
                                        />

                                        {/* Media library */}
                                        <div className="mt-4">
                                            {mediaLoading ? (
                                                <div className="text-center py-4 text-gray-500 text-sm">
                                                    Loading images...
                                                </div>
                                            ) : (
                                                <MediaLibrary
                                                    media={mediaList}
                                                    deleteUrlBase={`/hex-media/${hex.id}`}
                                                    onDelete={handleMediaDelete}
                                                    canDelete={true}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </aside>
                            </div>
                        ) : (
                            /* No pages exist yet - show empty state */
                            <div className="max-w-4xl">
                                <div className="bg-slate-800 rounded-lg p-8 border border-slate-700 text-center">
                                    <p className="text-gray-300 mb-4">
                                        This hex doesn't have any pages yet.
                                    </p>
                                    <button
                                        onClick={handleShowCreateFirstPageModal}
                                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                                    >
                                        Create First Page
                                    </button>
                                </div>
                            </div>
                )}

                <NewPageModal
                    isOpen={showNewPageModal}
                    onClose={() => {
                        setShowNewPageModal(false);
                        setNewPageName('');
                        setNewPageChapterId(null);
                    }}
                    onSubmit={handleCreatePage}
                    pageName={newPageName}
                    setPageName={setNewPageName}
                    isCreating={isCreatingPage}
                />
            </div>

            {/* Image size selector modal - shown when dropping an image */}
            {pendingImageDrop && (
                <ImageSizeSelector
                    media={pendingImageDrop.media}
                    position={pendingImageDrop.position}
                    onSelect={handleImageSizeSelect}
                    onCancel={handleImageDropCancel}
                />
            )}

            {/* Tile Selection Modal */}
            {showTileModal && (
                <TileSelectionModal
                    hexId={hex.id}
                    currentDomain={hex.domain as Domain | undefined}
                    currentTileImage={hex.tileImage}
                    onClose={() => setShowTileModal(false)}
                />
            )}

            <BookModal
                isOpen={showBookModal}
                onClose={() => {
                    setShowBookModal(false);
                    setEditingBook(null);
                }}
                onSubmit={handleSaveBook}
                editingBook={editingBook}
                bookTitle={bookTitle}
                setBookTitle={setBookTitle}
                bookDescription={bookDescription}
                setBookDescription={setBookDescription}
                bookHue={bookHue}
                setBookHue={setBookHue}
                isSaving={isSavingBook}
            />

            <ChapterModal
                isOpen={showChapterModal}
                onClose={() => {
                    setShowChapterModal(false);
                    setEditingChapter(null);
                    setEditingChapterBookId(null);
                }}
                onSubmit={handleSaveChapter}
                editingChapter={editingChapter}
                chapterTitle={chapterTitle}
                setChapterTitle={setChapterTitle}
                isSaving={isSavingChapter}
            />
        </HexLayout>
    );
}
